import React, { useCallback, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import SydInput from '../../../../commonDesign/SydInput'
import Switch from '../../../../molecules/Switch'
import Icon from '../../../../atoms/Icon'
import { ICON_NAMES } from '../../../../../constants'
import {
  ALL_ACTIVITY_TYPE_VALUES,
  CheckboxPopover,
  RowTypeDetail
} from './rowComponents'

const ITEM_TYPE = 'ROW_ITEM'

function DraggableRow ({ row, index, activityRows, onToggle, onNameChange, onRowFieldChange, onMoveItem }) {
  const ref = useRef(null)
  const [popoverAnchor, setPopoverAnchor] = useState(null)

  const [{ handlerId }, drop] = useDrop({
    accept: ITEM_TYPE,
    collect (monitor) {
      return { handlerId: monitor.getHandlerId() }
    },
    hover (item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const rect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (rect.bottom - rect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - rect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      onMoveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    }
  })

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: () => ({ id: row.key, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  })

  drag(drop(ref))

  const enabled = row.enabled !== false

  const handleChipAreaClick = useCallback((e) => {
    e.stopPropagation()
    setPopoverAnchor(e.currentTarget)
  }, [])

  const handlePopoverClose = useCallback(() => {
    setPopoverAnchor(null)
  }, [])

  const activityTypeItems = useMemo(
    () => ALL_ACTIVITY_TYPE_VALUES.map(t => ({ key: t, label: t })),
    []
  )
  const activityTypeSelected = useMemo(
    () => new Set(row.activityTypes || []),
    [row.activityTypes]
  )
  const handleActivityTypeToggle = useCallback((type) => {
    const current = new Set(row.activityTypes || [])
    if (current.has(type)) { current.delete(type) } else { current.add(type) }
    onRowFieldChange(index, 'activityTypes', [...current])
  }, [index, row.activityTypes, onRowFieldChange])

  const mvChangeItems = useMemo(
    () => (activityRows || []).map(r => ({ key: r.key, label: r.name })),
    [activityRows]
  )
  const mvChangeSelected = useMemo(() => {
    if (!Array.isArray(row.includedActivityKeys)) {
      return new Set((activityRows || []).map(r => r.key))
    }
    return new Set(row.includedActivityKeys)
  }, [row.includedActivityKeys, activityRows])

  const handleMvChangeToggle = useCallback((key) => {
    const allKeys = (activityRows || []).map(r => r.key)
    const wasAll = !Array.isArray(row.includedActivityKeys)
    const current = wasAll ? new Set(allKeys) : new Set(row.includedActivityKeys)

    if (current.has(key)) { current.delete(key) } else { current.add(key) }

    const newKeys = [...current]
    const isAll = newKeys.length === allKeys.length &&
      allKeys.every(k => current.has(k))
    onRowFieldChange(index, 'includedActivityKeys', isAll ? null : newKeys)
  }, [index, row.includedActivityKeys, activityRows, onRowFieldChange])

  const showActivityPopover = row.rowType === 'activity'
  const showMvChangePopover = row.rowType === 'derived' && row.derivation === 'mvChange'

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        padding: '6px 4px',
        borderBottom: '1px solid var(--color-neutral-100)',
        opacity: isDragging ? 0.4 : enabled ? 1 : 0.5,
        transition: 'opacity 0.15s'
      }}
    >
      <div style={{ cursor: 'grab', display: 'flex', alignItems: 'center', flexShrink: 0, paddingTop: '6px' }}>
        <Icon name={ICON_NAMES.dragIndicator} customSize='1.2rem' />
      </div>
      <div style={{ flexShrink: 0, paddingTop: '4px' }}>
        <Switch
          checked={enabled}
          onChange={() => onToggle(index)}
          size='small'
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <SydInput
          value={row.name}
          onChange={(e) => onNameChange(index, e.target.value)}
          size='sm'
          style={{ width: '100%' }}
        />
        <RowTypeDetail
          row={row}
          activityRows={activityRows}
          onChipAreaClick={handleChipAreaClick}
        />
      </div>
      {showActivityPopover && (
        <CheckboxPopover
          anchorEl={popoverAnchor}
          open={!!popoverAnchor}
          onClose={handlePopoverClose}
          heading='Activity Types'
          items={activityTypeItems}
          selectedKeys={activityTypeSelected}
          onToggle={handleActivityTypeToggle}
          minSelected={1}
        />
      )}
      {showMvChangePopover && (
        <CheckboxPopover
          anchorEl={popoverAnchor}
          open={!!popoverAnchor}
          onClose={handlePopoverClose}
          heading='Include in Formula'
          items={mvChangeItems}
          selectedKeys={mvChangeSelected}
          onToggle={handleMvChangeToggle}
        />
      )}
    </div>
  )
}

DraggableRow.propTypes = {
  row: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  activityRows: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
  onNameChange: PropTypes.func.isRequired,
  onRowFieldChange: PropTypes.func.isRequired,
  onMoveItem: PropTypes.func.isRequired
}

function RowEditor ({ rows, onChange }) {
  const activityRows = useMemo(
    () => (rows || []).filter(r => r.rowType === 'activity'),
    [rows]
  )

  const handleToggle = useCallback((index) => {
    const next = [...rows]
    next[index] = { ...next[index], enabled: next[index].enabled === false }
    onChange(next)
  }, [rows, onChange])

  const handleNameChange = useCallback((index, name) => {
    const next = [...rows]
    next[index] = { ...next[index], name }
    onChange(next)
  }, [rows, onChange])

  const handleRowFieldChange = useCallback((index, field, value) => {
    const next = [...rows]
    next[index] = { ...next[index], [field]: value }
    onChange(next)
  }, [rows, onChange])

  const handleMoveItem = useCallback((dragIndex, hoverIndex) => {
    const next = [...rows]
    const [moved] = next.splice(dragIndex, 1)
    next.splice(hoverIndex, 0, moved)
    onChange(next)
  }, [rows, onChange])

  if (!rows?.length) {
    return (
      <div style={{ color: 'var(--text-color-subtle)', fontSize: 'var(--font-size-sm)', padding: '8px 0' }}>
        No rows configured. Use &quot;Load Preset&quot; above to get started.
      </div>
    )
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div>
        {rows.map((row, i) => (
          <DraggableRow
            key={row.key}
            row={row}
            index={i}
            activityRows={activityRows}
            onToggle={handleToggle}
            onNameChange={handleNameChange}
            onRowFieldChange={handleRowFieldChange}
            onMoveItem={handleMoveItem}
          />
        ))}
      </div>
    </DndProvider>
  )
}

RowEditor.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired
}

export default RowEditor
